import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, ArrowRight } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user && typeof user === "object") return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email.trim().toLowerCase(), password);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    navigate(res.user.onboarding_completed ? "/dashboard" : "/onboarding", { replace: true });
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-5 py-10">
      <div className="ascend-atmosphere" />
      <Toaster richColors position="top-center" />

      <div className="w-full max-w-md fade-up">
        <div className="flex items-center gap-2 justify-center mb-6">
          <img
            src="/logo-128.png?v=dragon-padded"
            alt=""
            width={64}
            height={64}
            className="w-16 h-16 rounded-2xl select-none soft-pulse"
            draggable={false}
            data-testid="brand-logo"
          />
          <div className="text-center">
            <div className="font-display text-3xl" style={{ color: "var(--text)" }}>Ascend</div>
            <div className="font-hand text-sm -mt-1" style={{ color: "var(--accent)" }}>welcome back</div>
          </div>
        </div>

        <div className="card-soft p-7 sm:p-9">
          <h1 className="font-display text-3xl mb-1" style={{ color: "var(--text)" }}>Sign in</h1>
          <p className="text-sm text-muted mb-6">Pick up where you left off.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted mb-1 block">Email</label>
              <input
                type="email"
                className="input-soft"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email-input"
                placeholder="you@somewhere.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted mb-1 block">Password</label>
              <input
                type="password"
                className="input-soft"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="text-sm px-3 py-2 rounded-lg"
                   style={{ background: "color-mix(in oklab, var(--primary) 10%, transparent)", color: "var(--accent)" }}
                   data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="login-submit-btn"
            >
              {busy ? "Signing in…" : "Sign in"}
              <ArrowRight size={16} strokeWidth={1.75} />
            </button>
          </form>

          <div className="divider-soft my-6" />
          <p className="text-sm text-center text-muted">
            First time here?{" "}
            <Link to="/signup" className="font-semibold" style={{ color: "var(--primary)" }} data-testid="link-signup">
              Create an account
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-muted mt-6">
          A quiet place for your dream. No AI. No noise.
        </p>
      </div>
    </div>
  );
}
